import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { of } from 'rxjs';
import { NumberUtils, RxUtils, Tr } from '@utils';
import { useService } from '@hooks';
import { ChargerType, PortalRoutePath } from '@models';
import {
	siteService,
	simulationService,
	ParkingAreaInfrastructure,
	notificationService,
	quotationService,
	productService,
	ChargerCurrentType,
	ChargerUsingType
} from '@services';
import { Box, Card, Grid, Stack, Typography } from '@mui/material';
import { PortalPageTitle } from '@components/portal/common';
import { EntityListItemCell, RouterLinkButton } from '@components/common';
import { ILocationChargerCardProps, LocationChargerCard } from '@components/portal/locations';
import ErrorIcon from '@mui/icons-material/Error';

const paddingX = 9;
const SLOW_CHARGING_KW = 11;
const FAST_CHARGING_KW = 50;

export const ParkingAreaInfrastructureSizingUserInput: FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation('portal');
	const { siteId = '', simulationId = '', parkAreaId = '' } = useParams<{ siteId: string; simulationId: string; parkAreaId: string }>();
	const [powerPerChargerForUserInput, setPowerPerChargerForUserInput] = useState(0);
	const [deltaForUserInput, setDeltaForUserInput] = useState(0);
	const [evCount, setEvCount] = useState(0);
	const [parkingAreaInfra, setParkingAreaInfra] = useState<ParkingAreaInfrastructure>();
	const [acChargerImage, setAcChargerImage] = useState('');
	const [dcChargerImage, setDcChargerImage] = useState('');
	const [cpoChargerImage, setCpoChargerImage] = useState('');

	const { data: site } = useService(() => (siteId ? siteService.get(siteId) : of()), [siteId]);
	const { data: quotation } = useService(() => quotationService.getLatest());
	const { data: simulation } = useService(() => (simulationId ? simulationService.get(simulationId) : of()), [simulationId]);
	const { data: parkArea, trigger: fetchParkArea } = useService(
		() => (siteId && parkAreaId ? siteService.parkArea(siteId).get(parkAreaId) : of()),
		[siteId, parkAreaId]
	);
	const { data: parkAreaQuotation, trigger: fetchParkAreaQuotation } = useService(
		() => (quotation && parkAreaId ? quotationService.quotationItem(quotation.id).getItemsForParkingArea(parkAreaId) : of()),
		[quotation, parkAreaId]
	);

	const onUserInput = (values: ILocationChargerCardProps['values']) => {
		siteId &&
			parkAreaId &&
			RxUtils.promisify(
				siteService.parkArea(siteId).update(parkAreaId, {
					finalACToBeInstalled: values.AC,
					finalDCToBeInstalled: values.DC,
					finalCPOToBeInstalled: values['Home Wallbox']
				}),
				() => (
					fetchParkArea(),
					fetchParkAreaQuotation(),
					notificationService.sendSuccess(t('location.site-infra.user-input-success-message'))
				)
			);
	};

	useEffect(() => {
		const paInfra = (
			simulation?.infrastructure.siteInfrastructure.find(({ site: { id } }) => id === siteId)?.parkingAreaInfrastructure || []
		).find(({ parkingAreaId }) => parkingAreaId === parkAreaId);

		setParkingAreaInfra(paInfra);
		setEvCount(Object.values(paInfra?.sumOfParkingSlot || {}).reduce((acc, cur) => acc + cur, 0));
	}, [simulation, siteId, parkAreaId]);

	useEffect(() => {
		if (parkArea) {
			const powerAvailable = parkArea.powerAvailableForCharging || 0;
			const finalACToBeInstalled = parkArea.finalACToBeInstalled || 0;
			const finalDCToBeInstalled = parkArea.finalDCToBeInstalled || 0;
			const finalCPOToBeInstalled = parkArea.finalCPOToBeInstalled || 0;
			const slowChargersCount = finalACToBeInstalled + finalCPOToBeInstalled;
			const totalChargerCount = slowChargersCount + finalDCToBeInstalled;
			const powerUsedByChargingIncludingFast = slowChargersCount * SLOW_CHARGING_KW + finalDCToBeInstalled * FAST_CHARGING_KW;

			setDeltaForUserInput(powerAvailable - powerUsedByChargingIncludingFast);
			setPowerPerChargerForUserInput(Math.floor(powerAvailable / totalChargerCount) || 0);
		}
	}, [parkArea]);

	useEffect(() => {
		RxUtils.promisify(productService.getImageForChargePointType(ChargerCurrentType.AC), setAcChargerImage);
		RxUtils.promisify(productService.getImageForChargePointType(ChargerCurrentType.DC), setDcChargerImage);
		RxUtils.promisify(productService.getImageForChargePointType(ChargerCurrentType.DC, ChargerUsingType.PUBLIC), setCpoChargerImage);
	}, []);

	return (
		<Stack direction="column" spacing={2} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5, overflowY: 'auto' }}>
			<PortalPageTitle
				title={
					<Tr.Portal
						path="location.site-infra.title"
						options={{ name: [site?.name, parkArea?.name].filter(Boolean).join(' - ') }}
					/>
				}
				paddingX={3}
				onBackButtonClick={() => navigate(-1)}
			/>

			{parkArea ? (
				<Box sx={{ display: 'flex', flexGrow: 1, padding: paddingX, paddingBottom: 3 }}>
					<Card elevation={0} variant="outlined" sx={{ flexGrow: 1, width: 1.0, overflowY: 'auto' }}>
						<Stack direction="column" sx={{ flexGrow: 1, height: 1.0 }}>
							<Card elevation={0} variant="outlined" sx={{ paddingX: 4, paddingY: 2.5 }}>
								<Grid container direction="row" alignItems="center">
									<Grid item xs={7.5}>
										<Typography>{parkArea.name || ''}</Typography>
									</Grid>
									<Grid item xs={1.5}>
										<EntityListItemCell title={<Tr.Portal path="location.site-infra.parking-slots" />}>
											<Typography>
												{NumberUtils.formatAsNumber(parkArea.parkingSlotsNumber, { decimalScale: 0 })}
											</Typography>
										</EntityListItemCell>
									</Grid>
									<Grid item xs={1.5}>
										<EntityListItemCell title={<Tr.Portal path="location.site-infra.number-of-ev" />}>
											<Typography>{NumberUtils.formatAsNumber(evCount, { decimalScale: 0 })}</Typography>
										</EntityListItemCell>
									</Grid>
									<Grid item xs={1.5}>
										<EntityListItemCell title={<Tr.Portal path="location.site-infra.power-available" />}>
											<Typography>
												{NumberUtils.formatAsNumber(parkArea.powerAvailableForCharging, {
													decimalScale: 0,
													suffix: 'kW'
												})}
											</Typography>
										</EntityListItemCell>
									</Grid>
								</Grid>
							</Card>

							<Grid container sx={{ flexGrow: 1, paddingY: 4 }}>
								<Grid item xs={4}>
									<LocationChargerCard
										title={<Tr.Portal path="location.site-infra.current-status" />}
										sx={{ borderRightWidth: 1, borderRightStyle: 'solid', borderRightColor: 'grey.400' }}
										values={{
											[ChargerType.AC]: parkArea.currentACInstalled || 0,
											[ChargerType.DC]: parkArea.currentDCInstalled || 0,
											[ChargerType.CPO]: parkArea.currentCPOInstalled || 0
										}}
										images={{
											[ChargerType.AC]: acChargerImage,
											[ChargerType.DC]: dcChargerImage,
											[ChargerType.CPO]: cpoChargerImage
										}}
									/>
								</Grid>

								<Grid item xs={4}>
									<LocationChargerCard
										title={<Tr.Portal path="location.site-infra.voltaage-recommended-to-install" />}
										sx={{ borderRightWidth: 1, borderRightStyle: 'solid', borderRightColor: 'grey.400' }}
										values={{
											[ChargerType.AC]: parkingAreaInfra?.voltaageRecommendationACChargers || 0,
											[ChargerType.DC]: 0,
											[ChargerType.CPO]: 0
										}}
										images={{
											[ChargerType.AC]: acChargerImage,
											[ChargerType.DC]: dcChargerImage,
											[ChargerType.CPO]: cpoChargerImage
										}}
										powerPerCharger={parkingAreaInfra?.powerAvailableForEachSocketExcludingFast || 0}
										deltaPower={parkingAreaInfra?.delta || 0}
									/>
								</Grid>

								<Grid item xs={4}>
									<LocationChargerCard
										title={<Tr.Portal path="location.site-infra.final-choice-to-install" />}
										values={{
											[ChargerType.AC]: parkArea.finalACToBeInstalled || 0,
											[ChargerType.DC]: parkArea.finalDCToBeInstalled || 0,
											[ChargerType.CPO]: parkArea.finalCPOToBeInstalled || 0
										}}
										images={{
											[ChargerType.AC]: acChargerImage,
											[ChargerType.DC]: dcChargerImage,
											[ChargerType.CPO]: cpoChargerImage
										}}
										powerPerCharger={powerPerChargerForUserInput}
										deltaPower={deltaForUserInput}
										editable
										onEdit={onUserInput}
									/>
								</Grid>
							</Grid>
						</Stack>
					</Card>
				</Box>
			) : null}

			<Stack direction="column" spacing={1} alignItems="center" sx={{ paddingX }}>
				<RouterLinkButton
					data-testid="show-quotation-navigata-button"
					variant="contained"
					disabled={!parkAreaQuotation?.length}
					sx={{ textTransform: 'uppercase', maxWidth: 'fit-content' }}
					href={`/portal/${PortalRoutePath.QUOTATION}?site=${siteId}&park-area=${parkAreaId}`}
				>
					<Tr.Portal path="location.site-infra.show-quotation" />
				</RouterLinkButton>
				<Typography sx={{ height: 24 }}>
					{!parkAreaQuotation?.length ? (
						<Stack direction="row" spacing={1} sx={{ color: 'warning.900' }}>
							<ErrorIcon color="inherit" fontSize="small" />
							<Typography sx={{ color: 'inherit' }}>
								<Tr.Portal path="location.site-infra.enter-final-choice-first" />
							</Typography>
						</Stack>
					) : null}
				</Typography>
			</Stack>
		</Stack>
	);
};
